import { apiInstance } from "@/utils/axios";
import { LOCALSTORAGE } from "@/utils/constants";
import {
  setupAxiosInterceptors,
  setupRefreshTimer,
} from "@/utils/tokenRefresh";
import * as Sentry from "@sentry/vue";
import { defineStore } from "pinia";
import { computed, inject, ref } from "vue";

// Add function to decode JWT token
function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = window.atob(base64);
    const payload = JSON.parse(jsonPayload);

    // Validate required fields
    if (
      !payload.sub ||
      !payload.iat ||
      !payload.exp ||
      !payload.nbf ||
      !payload.jti
    ) {
      console.warn("JWT token missing required fields");
    }

    return payload;
  } catch (error) {
    console.error("Error parsing JWT:", error);
    return null;
  }
}

// Add password validation function
function validatePassword(password) {
  const minLength = 8;
  const maxLength = 64;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[@$!%*?&]/.test(password);

  if (password.length < minLength) {
    throw new Error(`Password must be at least ${minLength} characters long`);
  }
  if (password.length > maxLength) {
    throw new Error(`Password must not exceed ${maxLength} characters`);
  }
  if (!hasUpperCase) {
    throw new Error("Password must contain at least one uppercase letter");
  }
  if (!hasLowerCase) {
    throw new Error("Password must contain at least one lowercase letter");
  }
  if (!hasNumbers) {
    throw new Error("Password must contain at least one number");
  }
  if (!hasSpecialChar) {
    throw new Error(
      "Password must contain at least one special character (@$!%*?&)"
    );
  }
}

export const useUserAuthStore = defineStore("userAuthStore", () => {
  const accessToken = ref(
    localStorage.getItem(LOCALSTORAGE.USER_ACCESS_TOKEN) || null
  );
  const userId = ref(localStorage.getItem(LOCALSTORAGE.USER_ID) || null);
  const companyId = ref(localStorage.getItem(LOCALSTORAGE.COMPANY_ID) || null);
  const defaultAccess = ref(
    JSON.parse(localStorage.getItem(LOCALSTORAGE.DEFAULT_ACCESS) || "{}")
  );
  const availableIds = ref({
    company_ids: JSON.parse(
      localStorage.getItem(LOCALSTORAGE.COMPANY_IDS) || "[]"
    ),
    project_ids: JSON.parse(
      localStorage.getItem(LOCALSTORAGE.PROJECT_IDS) || "[]"
    ),
    unit_ids: JSON.parse(localStorage.getItem(LOCALSTORAGE.UNIT_IDS) || "[]"),
  });
  // Add refs for current selections
  const currentUnit = ref(
    JSON.parse(localStorage.getItem(LOCALSTORAGE.CURRENT_UNIT) || "null")
  );
  const currentProject = ref(
    JSON.parse(localStorage.getItem(LOCALSTORAGE.CURRENT_PROJECT) || "null")
  );
  const currentCompany = ref(
    JSON.parse(localStorage.getItem(LOCALSTORAGE.CURRENT_COMPANY) || "null")
  );

  const user = ref(null);
  const isChangePasswordLoading = ref(false);
  const isRefreshingToken = ref(false);
  const refreshTokenTimeout = ref(null);
  const isInitialized = ref(false);

  // Login, and or hard refresh page
  // Everytime you hard refresh page, generate a new token using older token
  // Pass this data inside the token
  // Store companies: []
  // Store projects: []
  // Store units: []
  // Store permissions: []
  // Building casl layer

  const signin = ref({
    user: {
      email: "",
      password: "",
      remember_me: false,
    },
    isLoading: false,
    response: null,
  });

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value);
  const getSigninResponse = computed(() => signin.value.response);
  const isSigninLoading = computed(() => signin.value.isLoading);
  const getUserId = computed(() => userId.value);
  const isRefreshingTokenStatus = computed(() => isRefreshingToken.value);

  // Add new computed properties
  const getAuthHeaders = computed(() => ({
    Authorization: accessToken.value ? `Bearer ${accessToken.value}` : "",
    "X-Company-ID": companyId.value || "",
    "Content-Type": "application/json",
  }));

  // Move posthog to store level
  const posthog = inject("posthog", null);

  // Initialize interceptors immediately instead of using onMounted
  setupAxiosInterceptors(false, false); // false for user, false for contractor

  // Initialize auth state
  async function initializeAuth() {
    if (isInitialized.value) return;

    try {
      if (accessToken.value) {
        // Check if token is expired
        const tokenData = parseJwt(accessToken.value);
        const now = Math.floor(Date.now() / 1000);

        if (tokenData && tokenData.exp < now) {
          // Token is expired, try to refresh it
          try {
            await refreshToken();
          } catch (error) {
            // If refresh fails, clear auth data
            clearAuthData();
            return;
          }
        }

        // Set up API headers
        apiInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${accessToken.value}`;

        // Fetch user data
        const userData = await getAuthenticatedUser();
        if (userData) {
          user.value = userData;
          if (userData.role) {
            localStorage.setItem(LOCALSTORAGE.USER_ROLE, userData.role);
          }
          if (refreshTokenTimeout.value) {
            clearTimeout(refreshTokenTimeout.value);
          }
          refreshTokenTimeout.value = setupRefreshTimer(
            accessToken.value,
            refreshToken,
            false
          );
        }
      }
    } catch (error) {
      console.error("Auth initialization failed:", error);
      clearAuthData();
    } finally {
      isInitialized.value = true;
    }
  }

  // Actions
  async function signIn() {
    signin.value.isLoading = true;

    try {
      const response = await apiInstance.post(
        `api/user/auth/login`,
        {
          email: signin.value.user.email,
          password: signin.value.user.password,
          remember_me: signin.value.user.remember_me,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      console.log("Login response:", response.data);

      // Store the raw response first
      signin.value.response = response.data;

      // Check if the response indicates an error condition
      if (
        response.data.status === "error" ||
        response.data.status === "WRONG_CREDENTIALS_ERROR"
      ) {
        throw new Error(response.data.message || "Login failed");
      }

      const {
        access_token,
        user: userData,
        companies,
        current_company,
      } = response.data;

      // More detailed validation of the response
      if (!access_token) {
        console.error("No access token in response:", response.data);
        throw new Error("Login successful but no access token received");
      }

      if (!userData) {
        console.error("No user data in response:", response.data);
        throw new Error("Login successful but no user data received");
      }

      // Set access token first to ensure headers are set
      setAccessToken(access_token);

      // Set user data
      user.value = userData;
      isInitialized.value = true;

      // Store user role in localStorage
      if (userData?.role) {
        localStorage.setItem(LOCALSTORAGE.USER_ROLE, userData.role);
      }

      // Handle current company from response
      if (current_company) {
        setCurrentCompany(current_company);
      } else if (companies?.length > 0) {
        // If no current company but companies exist, use the first one
        setCurrentCompany(companies[0]);
      }

      // Only call posthog if it exists
      if (posthog) {
        posthog.identify(userData.id, {
          email: userData.email,
          name: userData.name,
        });
      }

      Sentry.setUser({
        id: userData.id,
        email: userData.email,
      });

      return response.data;
    } catch (error) {
      console.error("Login error:", error);

      // Only clear auth data if it's an authentication error
      if (
        error.response?.status === 401 ||
        error.message?.includes("access token") ||
        error.message?.includes("credentials")
      ) {
        clearAuthData();
      }

      signin.value.response = {
        status: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Login failed. Please try again.",
      };

      throw error;
    } finally {
      signin.value.isLoading = false;
    }
  }

  async function refreshToken() {
    if (!accessToken.value) {
      throw new Error("No access token available for refresh");
    }

    try {
      isRefreshingToken.value = true;
      const response = await apiInstance.post(
        "api/user/auth/refresh",
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken.value}`,
          },
        }
      );

      const { access_token, user: userData } = response.data;
      if (!access_token) {
        throw new Error("No access token received during refresh");
      }

      setAccessToken(access_token);

      if (userData) {
        user.value = userData;
        if (userData.role) {
          localStorage.setItem(LOCALSTORAGE.USER_ROLE, userData.role);
        }
      }

      if (refreshTokenTimeout.value) {
        clearTimeout(refreshTokenTimeout.value);
      }
      refreshTokenTimeout.value = setupRefreshTimer(
        access_token,
        refreshToken,
        false
      );

      return response.data;
    } catch (error) {
      console.error("Token refresh failed:", error);

      if (error.response) {
        switch (error.response.status) {
          case 401:
            clearAuthData();
            break;
          case 403:
            console.error(
              "Access denied. Please use the appropriate portal for your role."
            );
            break;
          case 500:
            console.error("Server error occurred during token refresh");
            break;
          default:
            console.error(
              "Token refresh failed:",
              error.response.data?.message || error.message
            );
        }
      }

      throw error;
    } finally {
      isRefreshingToken.value = false;
    }
  }

  async function logout(skipApiCall = false) {
    try {
      if (!skipApiCall) {
        await apiInstance.post(
          "api/user/auth/logout",
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${accessToken.value}`,
            },
          }
        );
      }
      clearAuthData();
    } catch (error) {
      clearAuthData();
      throw error;
    }
  }

  async function getAuthenticatedUser() {
    try {
      const response = await apiInstance.get("api/user/auth/me", {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${accessToken.value}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async function changePassword(payload) {
    try {
      isChangePasswordLoading.value = true;
      const response = await apiInstance.post(
        "api/user/auth/change-password",
        {
          current_password: payload.current_password,
          new_password: payload.new_password,
          new_password_confirmation: payload.new_password_confirmation,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken.value}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      isChangePasswordLoading.value = false;
    }
  }

  async function forgetPassword(email) {
    try {
      const response = await apiInstance.post(
        "api/user/auth/forget-password",
        {
          email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async function resetPassword(payload) {
    try {
      // Validate password format before API call
      validatePassword(payload.password);

      const response = await apiInstance.post(
        "api/user/auth/reset-password",
        {
          token: payload.token,
          email: payload.email,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      let message =
        error.response?.data?.message ||
        error.message ||
        "Password reset failed";

      // Handle specific error cases
      if (error.message.includes("Password must")) {
        message = error.message; // Show the validation error
      }

      return {
        success: false,
        error: message,
      };
    }
  }

  function clearAuthData() {
    if (refreshTokenTimeout.value) {
      clearTimeout(refreshTokenTimeout.value);
      refreshTokenTimeout.value = null;
    }

    // Clear all auth-related state
    accessToken.value = null;
    userId.value = null;
    companyId.value = null;
    defaultAccess.value = {};
    availableIds.value = {
      company_ids: [],
      project_ids: [],
      unit_ids: [],
    };
    currentUnit.value = null;
    currentProject.value = null;
    currentCompany.value = null;
    user.value = null;

    // Clear all auth-related localStorage
    localStorage.removeItem(LOCALSTORAGE.USER_ACCESS_TOKEN);
    localStorage.removeItem(LOCALSTORAGE.USER_ID);
    localStorage.removeItem(LOCALSTORAGE.COMPANY_ID);
    localStorage.removeItem(LOCALSTORAGE.USER_ROLE);
    localStorage.removeItem(LOCALSTORAGE.DEFAULT_ACCESS);
    localStorage.removeItem(LOCALSTORAGE.COMPANY_IDS);
    localStorage.removeItem(LOCALSTORAGE.PROJECT_IDS);
    localStorage.removeItem(LOCALSTORAGE.UNIT_IDS);
    localStorage.removeItem("current_unit");
    localStorage.removeItem("current_project");
    localStorage.removeItem("current_company");
    localStorage.removeItem("company-id");
    localStorage.removeItem("project_id");

    // Clear API headers
    delete apiInstance.defaults.headers.common["Authorization"];
    delete apiInstance.defaults.headers.common["X-Company-ID"];
  }

  function updateSigninUserData(payload) {
    signin.value.user = { ...signin.value.user, ...payload };
  }

  function setAccessToken(token) {
    if (!token) return;

    accessToken.value = token;
    localStorage.setItem(LOCALSTORAGE.USER_ACCESS_TOKEN, token);
    apiInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const tokenData = parseJwt(token);
    if (tokenData) {
      const {
        user_id,
        role,
        company_ids,
        project_ids,
        unit_ids,
        default_access,
      } = tokenData;

      if (user_id) {
        userId.value = user_id;
        localStorage.setItem(LOCALSTORAGE.USER_ID, user_id.toString());
      }

      if (role) {
        localStorage.setItem(LOCALSTORAGE.USER_ROLE, role);
      }

      // Store company_ids
      if (company_ids) {
        availableIds.value.company_ids = company_ids;
        localStorage.setItem(
          LOCALSTORAGE.COMPANY_IDS,
          JSON.stringify(company_ids)
        );
      }

      // Store project_ids
      if (project_ids) {
        availableIds.value.project_ids = project_ids;
        localStorage.setItem(
          LOCALSTORAGE.PROJECT_IDS,
          JSON.stringify(project_ids)
        );
      }

      // Store unit_ids
      if (unit_ids) {
        availableIds.value.unit_ids = unit_ids;
        localStorage.setItem(LOCALSTORAGE.UNIT_IDS, JSON.stringify(unit_ids));
      }

      // Store default_access but don't override current selections
      if (default_access) {
        defaultAccess.value = default_access;
        localStorage.setItem(
          LOCALSTORAGE.DEFAULT_ACCESS,
          JSON.stringify(default_access)
        );

        // Set company ID if not already set
        if (default_access.company_id && !companyId.value) {
          companyId.value = default_access.company_id;
          localStorage.setItem(
            LOCALSTORAGE.COMPANY_ID,
            default_access.company_id.toString()
          );
          apiInstance.defaults.headers.common["X-Company-ID"] =
            default_access.company_id.toString();
        }

        // Only set current selections if they don't exist in localStorage
        const storedUnit = localStorage.getItem("current_unit");
        const storedProject = localStorage.getItem("current_project");
        const storedCompany = localStorage.getItem("current_company");

        if (!storedCompany && default_access.company_id) {
          setCurrentCompany({ id: default_access.company_id });
        }

        if (!storedProject && default_access.project_id) {
          setCurrentProject({ id: default_access.project_id });
        }

        if (!storedUnit && default_access.unit_id) {
          setCurrentUnit({ id: default_access.unit_id });
        }
      }

      // Setup refresh timer
      if (refreshTokenTimeout.value) {
        clearTimeout(refreshTokenTimeout.value);
      }
      refreshTokenTimeout.value = setupRefreshTimer(token, refreshToken, false);
    }
  }

  // Add functions to update current selections
  function setCurrentUnit(unit) {
    if (!unit || !unit.id) return;
    const unitId = typeof unit === "object" ? unit.id : parseInt(unit);
    currentUnit.value = unitId;
    localStorage.setItem("current_unit", String(unitId));
  }

  function setCurrentProject(project) {
    if (!project || !project.id) return;
    const projectId =
      typeof project === "object" ? project.id : parseInt(project);
    currentProject.value = projectId;
    localStorage.setItem("current_project", String(projectId));
    localStorage.setItem("project_id", String(projectId));
  }

  function setCurrentCompany(company) {
    if (!company) return;

    // Handle the company object structure from the API
    const companyIdValue = company.company_id;
    if (!companyIdValue) {
      console.error("Invalid company object:", company);
      return;
    }

    currentCompany.value = companyIdValue;
    companyId.value = companyIdValue;
    localStorage.setItem("current_company", String(companyIdValue));
    localStorage.setItem("company-id", String(companyIdValue));
    apiInstance.defaults.headers.common["X-Company-ID"] =
      String(companyIdValue);
  }

  return {
    // State
    accessToken,
    userId,
    companyId,
    signin,
    user,
    isChangePasswordLoading,
    isRefreshingToken,
    isInitialized,
    defaultAccess,
    availableIds,
    currentUnit,
    currentProject,
    currentCompany,

    // Getters
    isAuthenticated,
    getSigninResponse,
    isSigninLoading,
    getUserId,
    isRefreshingTokenStatus,
    getAuthHeaders,

    // Actions
    signIn,
    refreshToken,
    logout,
    clearAuthData,
    updateSigninUserData,
    setAccessToken,
    initializeAuth,
    getAuthenticatedUser,
    changePassword,
    forgetPassword,
    resetPassword,
    setCurrentUnit,
    setCurrentProject,
    setCurrentCompany,
  };
});
